import { effect, inject, Injectable, signal, untracked } from "@angular/core";
import { ConfigModel } from "../models/config";
import { BehaviorSubject, ReplaySubject, Subject } from "rxjs";
import { ConfigService as ApplicationConfigService } from "@dot/config";
import { DokaIdentityLoginService } from "@doka/identity-login";

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  public readonly defaultLanguage: string = "en_us";
  public appLanguages: string[] = [];
  public appLanguagesTranslated: {
    value: string;
    label: string;
  }[] = [];
  public readonly teams: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  public configChange: Subject<void> = new Subject();
  public isPrinterConnected: boolean;
  public configLoaded: ReplaySubject<boolean> = new ReplaySubject(1);
  public newOrder = new BehaviorSubject<boolean>(true);
  public configFinishedLoading: boolean = false;
  public token$ = signal<string>(null);
  private readonly configSnapshot = new Map<string, string>();

  private readonly identityLoginService: DokaIdentityLoginService = inject(DokaIdentityLoginService);
  private readonly applicationConfigService: ApplicationConfigService = inject(ApplicationConfigService);

  public readonly config$ = signal(new ConfigModel());

  constructor() {
    effect(async () => {
      if (this.identityLoginService.currentUser()) {
        if (!this.applicationConfigService.configsReadySig()) {
          await this.applicationConfigService.configReady();
        }

        untracked(() => {
          for (const key of Object.keys(this.config$())) {
            const value = this.applicationConfigService.get(key);
            if (value) {
              const parsedValue =
                typeof this.config$()[key] === "object" || Array.isArray(this.config$()[key])
                  ? JSON.parse(value)
                  : value;
              // TODO remove this 
              if (key === "reconItemColumns") {
                if (!parsedValue.find((column) => column.value === "quantityPosted")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue
                }
                if (!parsedValue.find((column) => column.value === "mobileReconditioningStatus")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
                if (!parsedValue.find((column) => column.value === "quantityDefectDokaDone")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
                if (!parsedValue.find((column) => column.value === "quantityDefectDokaPosted")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
                if (!parsedValue.find((column) => column.value === "quantityDefectCustomerDone")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
                if (!parsedValue.find((column) => column.value === "quantityDefectCustomerPosted")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
              }
              if (key === "reconSparePartColumns") {
                if (!parsedValue.find((column) => column.value === "quantityPosted")) {
                  this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
                  continue;
                }
              }
              this.config$()[key] = parsedValue;
            } else {
              this.applicationConfigService.set(key, JSON.stringify(this.config$()[key]));
            }
          }
        });
      }
    });

    effect(async () => {
      for (const key of Object.keys(this.config$())) {
        const value = this.config$()[key];
        const onlineVal = this.applicationConfigService.get(key);
        const serializedValue = typeof value === "object" ? JSON.stringify(value) : value.toString();
        if (serializedValue !== onlineVal) {
          await this.applicationConfigService.set(key, serializedValue);
        }
      }
    });
  }

  //Loading current configuration from local DB
  public getConfig(userConfig?: Map<string, string>): ConfigModel {
    if (!userConfig) {
      userConfig = this.applicationConfigService.userConfiguration$();
    }
    var config: ConfigModel = new ConfigModel();
    for (let key of ConfigModel.CONFIG_KEYS) {
      var currentConfigValue = "";
      if (key === "branchId") {
        currentConfigValue = userConfig.get("selectedBranchId");
      } else {
        currentConfigValue = userConfig.get(key);
      }
      console.log(userConfig.get("selectedBranchId"));

      try {
        if (key === "measurementSystem") {
          var configJSON = JSON.parse(currentConfigValue);
          configJSON = configJSON.replace("Metric", "metric");
          configJSON = configJSON.replace("Imperial", "imperial");
          config[key] = JSON.parse(configJSON);
        } else {
          config[key] = JSON.parse(currentConfigValue);
        }
      } catch {
        if (key === "clientid") {
        }
        config[key] = currentConfigValue;
      }
    }
    console.log(config);
    if (config.returnHighlighting == undefined) {
      config.returnHighlighting = "All";
    }

    this.cacheConfigSnapshot(config);
    return config;
  }

  //Saving config changes to DB and directly return the new config
  public async saveConfig(config: ConfigModel) {
    console.log("Saving Config:", JSON.parse(JSON.stringify(config)));
    // this.cookieService.set(
    //   "config",
    //   JSON.stringify(config) /* ,
    //   new Date("12-12-2020"),
    //   "/",
    //   "",
    //   true,
    //   "Strict" */
    // );
    try {
      for (const configKey of ConfigModel.CONFIG_KEYS) {
        await this.saveConfigKey(configKey, config[configKey]);
      }
    } catch (error) {
      console.error("Error saving config:", error);
    }
  }

  public deleteConfig() {
    console.log("DELETE COOKIES");
    this.config$.set(new ConfigModel());
  }

  /* Sets Global Font Size, either large or standard */
  // setFontSize(config: ConfigModel) {
  //   let element: HTMLHtmlElement;
  //   console.log(config.fontSize);
  //   if (config.fontSize === "standard") {
  //     element = document.getElementsByTagName("html")[0];
  //     element.style.fontSize = "1rem";
  //   } else {
  //     element = document.getElementsByTagName("html")[0];
  //     element.style.fontSize = "1.15rem";
  //   }
  // }

  public changeFont(fontSize?: string) {
    const fontS = fontSize ?? this.config$().fontSize;
    let element: HTMLHtmlElement;
    if (fontS === "standard") {
      element = document.getElementsByTagName("html")[0];
      element.style.fontSize = "100%";
    } else {
      element = document.getElementsByTagName("html")[0];
      element.style.fontSize = "110%";
    }
  }

  async reorderColumns(columnVariable: string, event) {
    let column = JSON.parse(this.applicationConfigService.get(columnVariable));
    const tempMovedElement = column[event.previousIndex];
    console.log(tempMovedElement);
    if (event.previousIndex < event.currentIndex) {
      for (let i = event.previousIndex; i <= event.currentIndex; i++) {
        column[i] = column[i + 1];
      }
    } else {
      for (let i = event.previousIndex; i >= event.currentIndex; i--) {
        column[i] = column[i - 1];
      }
    }
    column[event.currentIndex] = tempMovedElement;
    await this.applicationConfigService.set(columnVariable, JSON.stringify(column));
    this.newOrder.next(true);
  }

  setAllColumnsToVisible(config: ConfigModel) {
    for (let key in config) {
      if (key.toLowerCase().includes("columns".toLowerCase())) {
        for (let column of config[key]) {
          column.display = true;
        }
      }
    }
  }

  private async saveConfigKey(configKey: string, value: unknown) {
    if (value == null || value == undefined) {
      return;
    }

    const storageKey = this.getStorageKey(configKey);
    const serializedValue = this.serializeConfigValue(value);

    if (serializedValue == undefined) {
      return;
    }

    if (this.configSnapshot.get(storageKey) === serializedValue) {
      return;
    }

    await this.applicationConfigService.set(storageKey, serializedValue);
    this.configSnapshot.set(storageKey, serializedValue);
  }

  private cacheConfigSnapshot(config: ConfigModel) {
    for (const configKey of ConfigModel.CONFIG_KEYS) {
      const storageKey = this.getStorageKey(configKey);
      const serializedValue = this.serializeConfigValue(config[configKey]);

      if (serializedValue != undefined) {
        this.configSnapshot.set(storageKey, serializedValue);
      }
    }
  }

  private serializeConfigValue(value: unknown): string | undefined {
    if (value == null || value == undefined) {
      return undefined;
    }

    return typeof value === "object" ? JSON.stringify(value) : value.toString();
  }

  private getStorageKey(configKey: string): string {
    if (configKey === "branchId") {
      return "selectedBranchId";
    }

    return configKey;
  }
}
